import { FunctionComponent, MouseEventHandler } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { Link } from 'dibs-elements/exports/Link';
import { Header } from 'dibs-elements/exports/Header';
import { Badge } from 'dibs-elements/exports/Badge';
import { moreWaysToBrowseHeader } from '../../shared/sharedMessages';
import { SharedPopularSearchesLink } from '../SharedPopularSearchesLink/SharedPopularSearchesLink';
import { LINKS_BLOCK_DATA_TN } from '../../constants/sbConstants';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import styles from './SharedRespLinksBlock.scss';
import type { SharedRespLinksBlock_viewer } from './__generated__/SharedRespLinksBlock_viewer.graphql';
import type { SharedRespLinksBlock_itemSearch } from './__generated__/SharedRespLinksBlock_itemSearch.graphql';
import type { SharedRespLinksBlock_item } from './__generated__/SharedRespLinksBlock_item.graphql';

type Props = {
    viewer: SharedRespLinksBlock_viewer;
    itemSearch: SharedRespLinksBlock_itemSearch;
    item: SharedRespLinksBlock_item;
    links: Array<{ anchorText: string; url: string }>;
    title?: string;
    anchorTextPrefix?: string;
    onClick?: MouseEventHandler;
};

const MIN_LINK_COUNT = 3;

export const SharedRespLinksBlockComponent: FunctionComponent<Props> = ({
    viewer,
    itemSearch,
    item,
    links,
    title = moreWaysToBrowseHeader,
    anchorTextPrefix,
    onClick = () => {},
}) => {
    if (!links?.length) {
        return null;
    }

    const validLinks = links.filter(link => link.url && link.anchorText);

    if (validLinks.length < MIN_LINK_COUNT) {
        return null;
    }

    const { isPopularSearchesEnabled } = viewer;
    const { popularSearchesLink } = itemSearch || item;

    return (
        <HeadingLevel>
            {Heading => (
                <div className={styles.wrapper} data-tn={LINKS_BLOCK_DATA_TN}>
                    <Header title={title} htmlElementType={Heading} />
                    <div className={styles.links}>
                        {validLinks.map(({ anchorText, url }) => (
                            <Link
                                key={url}
                                href={url}
                                dataTn={`${LINKS_BLOCK_DATA_TN}-item`}
                                className={`${styles.link} ${styles.specify}`}
                                onClick={onClick}
                            >
                                <Badge type="pill" size="medium-regular" interactive>
                                    {anchorTextPrefix
                                        ? `${anchorTextPrefix} ${anchorText}`
                                        : anchorText}
                                </Badge>
                            </Link>
                        ))}
                    </div>
                    {isPopularSearchesEnabled && (
                        <SharedPopularSearchesLink popularSearchesLink={popularSearchesLink} />
                    )}
                </div>
            )}
        </HeadingLevel>
    );
};

export const SharedRespLinksBlock = createFragmentContainer(SharedRespLinksBlockComponent, {
    viewer: graphql`
        fragment SharedRespLinksBlock_viewer on Viewer {
            isPopularSearchesEnabled: featureFlag(feature: "popularSearches")
        }
    `,
    itemSearch: graphql`
        fragment SharedRespLinksBlock_itemSearch on ItemSearchQueryConnection {
            popularSearchesLink {
                ...SharedPopularSearchesLink_popularSearchesLink
            }
        }
    `,
    item: graphql`
        fragment SharedRespLinksBlock_item on Item {
            popularSearchesLink {
                ...SharedPopularSearchesLink_popularSearchesLink
            }
        }
    `,
    links: graphql`
        fragment SharedRespLinksBlock_links on InternalBuyLinkType @relay(plural: true) {
            url
            anchorText
        }
    `,
});
